/**
 * The camera — Task 11's own line: "the camera (pan/zoom) is a local CSS
 * transform over that SVG — free, no server round-trip past the one
 * initial fetch". This is deliberately NOT the app's Reanimated/Skia
 * camera: no physics, no gesture-handler, just a `transform` on the
 * inlined SVG element driven by wheel/drag, clamped to sane bounds. "The
 * venn is theirs, the window is yours" (the design's own line) — the
 * picture never changes, only how the visitor is looking at it.
 */

const MIN_SCALE = 0.5;
const MAX_SCALE = 4;

export interface Camera {
  /** Detach every listener this camera attached. */
  destroy(): void;
}

/** Wires wheel-to-zoom and drag-to-pan onto `viewport`, transforming
 *  `target`. Returns a handle to tear the listeners down (the closed/
 *  transient states remove the camera along with the picture). */
export function attachCamera(
  viewport: HTMLElement,
  target: HTMLElement | SVGElement,
): Camera {
  let scale = 1;
  let x = 0;
  let y = 0;
  let dragging = false;
  let lastX = 0;
  let lastY = 0;

  const apply = (): void => {
    target.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
  };

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const delta = -e.deltaY * 0.001;
    scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + delta * scale));
    apply();
  };

  const onPointerDown = (e: PointerEvent): void => {
    dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
    viewport.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (!dragging) return;
    x += e.clientX - lastX;
    y += e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    apply();
  };

  const onPointerUp = (): void => {
    dragging = false;
  };

  viewport.addEventListener('wheel', onWheel, { passive: false });
  viewport.addEventListener('pointerdown', onPointerDown);
  viewport.addEventListener('pointermove', onPointerMove);
  viewport.addEventListener('pointerup', onPointerUp);
  viewport.addEventListener('pointercancel', onPointerUp);

  return {
    destroy(): void {
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', onPointerUp);
      viewport.removeEventListener('pointercancel', onPointerUp);
    },
  };
}
